<template>
  <header class="app-topbar">
    <div class="app-topbar__inner">
      <!-- Left: sidebar toggle + brand -->
      <div class="app-topbar__left">
        <button
          type="button"
          class="app-topbar__sidebar-btn"
          :class="{ toggled: $sidebar.showSidebar }"
          aria-label="Toggle sidebar"
          @click="toggleSidebar"
        >
          <span class="bar"></span>
          <span class="bar"></span>
          <span class="bar"></span>
        </button>
        <router-link :to="{ name: 'Doctors' }" class="app-topbar__brand">
          <b>Booki</b>med
        </router-link>
      </div>

      <!-- Right: avatar dropdown -->
      <div class="app-topbar__right" ref="avatarRef">
        <button
          type="button"
          class="app-topbar__user"
          :class="{ 'is-open': isOpen }"
          :title="email"
          @click.stop="toggle"
        >
          <span v-if="!avatar" class="app-topbar__avatar-fallback">{{ initials }}</span>
          <img
            v-else
            :src="avatar"
            alt="avatar"
            class="app-topbar__avatar"
            referrerpolicy="no-referrer"
          />
          <span class="app-topbar__username">{{ displayName }}</span>
          <svg class="app-topbar__caret" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>

        <transition name="app-dropdown">
          <div v-show="isOpen" class="app-topbar__menu">
            <div class="app-topbar__menu-head">
              <div class="app-topbar__menu-name">{{ displayName || 'Admin' }}</div>
              <div v-if="email" class="app-topbar__menu-email">{{ email }}</div>
            </div>
            <button type="button" class="app-topbar__menu-item" @click="logout">
              Log out
            </button>
          </div>
        </transition>
      </div>
    </div>
  </header>
</template>

<script>
import Cookies from 'js-cookie';
import { mapActions, mapGetters } from 'vuex';

export default {
  data: () => ({
    isOpen: false,
  }),
  computed: {
    ...mapGetters('admin', ['fullName', 'email', 'avatar']),
    displayName() {
      if (this.fullName) return this.fullName;
      if (this.email) return this.email.split('@')[0];
      return 'Admin';
    },
    initials() {
      const name = this.displayName;
      if (!name) return 'A';
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.slice(0, 2).toUpperCase();
    },
  },
  methods: {
    ...mapActions('admin', ['getAdmin']),
    toggle() {
      this.isOpen = !this.isOpen;
    },
    close() {
      this.isOpen = false;
    },
    onClickOutside(e) {
      if (this.$refs.avatarRef && !this.$refs.avatarRef.contains(e.target)) this.close();
    },
    toggleSidebar() {
      this.$sidebar.displaySidebar(!this.$sidebar.showSidebar);
    },
    logout() {
      this.$store.commit('agencies/RESET_PERMISSIONS');
      this.$store.commit('employees/RESET_PERMISSIONS');
      const domain = window.location.hostname.split('.').slice(-2).join('.');
      Cookies.remove('adminToken', { path: '/', domain });
      Cookies.remove('vT', { path: '/', domain });
      this.$router.push({ name: 'Login' });
    },
  },
  mounted() {
    this.getAdmin();
    document.addEventListener('click', this.onClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onClickOutside);
  },
};
</script>

<style scoped lang="scss">
$topbar-bg: #5c9ad0;
$topbar-fg: #ffffff;

.app-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  background: $topbar-bg;
  color: $topbar-fg;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
  user-select: none;
}
.app-topbar__inner {
  height: 56px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.app-topbar__left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.app-topbar__sidebar-btn {
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  width: 36px;
  height: 36px;
  padding: 0 8px;
  border: 0;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  .bar {
    display: block;
    height: 2px;
    width: 18px;
    background: $topbar-fg;
    transition: transform 0.2s, opacity 0.2s;
  }

  &.toggled {
    .bar:nth-child(1) { transform: translateY(6px) rotate(45deg); }
    .bar:nth-child(2) { opacity: 0; }
    .bar:nth-child(3) { transform: translateY(-6px) rotate(-45deg); }
  }
}
.app-topbar__brand {
  font-family: 'Poppins', 'Source Sans Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 20px;
  font-weight: 400;
  color: $topbar-fg;
  text-decoration: none;
  letter-spacing: -0.01em;

  &:hover,
  &:focus {
    color: $topbar-fg;
    text-decoration: none;
  }

  b {
    font-weight: 700;
  }
}
.app-topbar__right {
  position: relative;
}
.app-topbar__user {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 4px 10px 4px 4px;
  border: 0;
  background: transparent;
  color: $topbar-fg;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover,
  &.is-open {
    background: rgba(255, 255, 255, 0.15);
  }
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
  }
}
.app-topbar__avatar,
.app-topbar__avatar-fallback {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: $topbar-fg;
  flex-shrink: 0;
  object-fit: cover;
}
.app-topbar__username {
  font-size: 13px;
  font-weight: 600;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.app-topbar__caret {
  width: 14px;
  height: 14px;
  transition: transform 0.2s;

  .app-topbar__user.is-open & {
    transform: rotate(180deg);
  }
}
.app-topbar__menu {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  min-width: 220px;
  background: $topbar-bg;
  color: $topbar-fg;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  z-index: 50;
}
.app-topbar__menu-head {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}
.app-topbar__menu-name {
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
}
.app-topbar__menu-email {
  font-size: 12px;
  opacity: 0.8;
  margin-top: 2px;
  word-break: break-all;
}
.app-topbar__menu-item {
  display: block;
  width: 100%;
  padding: 12px 16px;
  border: 0;
  background: transparent;
  color: $topbar-fg;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
}

.app-dropdown-enter-active,
.app-dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.app-dropdown-enter,
.app-dropdown-enter-from,
.app-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
